import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Navigation } from "lucide-react";
import { geocodePlace } from "@/lib/geocode";

// Fix default marker icons (CRA bundling)
const pinIcon = (color) =>
  L.divIcon({
    className: "",
    html: `<div style="background:${color};width:18px;height:18px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,.4)"></div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 18],
  });

export function RouteMap({ pickup, delivery, pickupCoord, deliveryCoord }) {
  const [from, setFrom] = useState(pickupCoord || null);
  const [to, setTo] = useState(deliveryCoord || null);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!pickupCoord && pickup) {
        const c = await geocodePlace(pickup);
        if (alive && c) setFrom(c);
      }
      if (!deliveryCoord && delivery) {
        const c = await geocodePlace(delivery);
        if (alive && c) setTo(c);
      }
    })();
    return () => {
      alive = false;
    };
  }, [pickup, delivery, pickupCoord, deliveryCoord]);

  const points = [from, to].filter(Boolean);
  const center = from || to || [41.9, 12.5]; // Italy fallback
  const place = encodeURIComponent(delivery || pickup || "Italia");
  const googleUrl =
    from && to
      ? `https://www.google.com/maps/dir/?api=1&origin=${from[0]},${from[1]}&destination=${to[0]},${to[1]}`
      : `https://www.google.com/maps/search/?api=1&query=${place}`;
  const appleUrl =
    from && to
      ? `https://maps.apple.com/?saddr=${from[0]},${from[1]}&daddr=${to[0]},${to[1]}`
      : `https://maps.apple.com/?q=${place}`;

  return (
    <div className="space-y-3">
      <div className="h-64 w-full overflow-hidden rounded-2xl border border-border" data-testid="route-map">
        <MapContainer key={`${pickup}|${delivery}`} center={center} zoom={points.length === 2 ? 6 : 8} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }}>
          <TileLayer
            attribution='&copy; OpenStreetMap'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {from ? (
            <Marker position={from} icon={pinIcon("#16A34A")}>
              <Popup>Carico: {pickup}</Popup>
            </Marker>
          ) : null}
          {to ? (
            <Marker position={to} icon={pinIcon("#DC2626")}>
              <Popup>Scarico: {delivery}</Popup>
            </Marker>
          ) : null}
          {points.length === 2 ? <Polyline positions={points} color="#0284C7" weight={4} /> : null}
        </MapContainer>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-muted-foreground">Apri su Maps:</span>
        <a
          href={googleUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="open-google-maps-button"
          className="inline-flex items-center gap-2 rounded-xl border border-primary px-4 py-2 text-sm font-bold text-primary hover:bg-secondary"
        >
          <Navigation className="h-4 w-4" /> Google Maps
        </a>
        <a
          href={appleUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="open-apple-maps-button"
          className="inline-flex items-center gap-2 rounded-xl border border-primary px-4 py-2 text-sm font-bold text-primary hover:bg-secondary"
        >
          <Navigation className="h-4 w-4" /> Apple Maps
        </a>
      </div>
    </div>
  );
}
